import React, { useState } from 'react'
import { Alert, Box, Button, Collapse, Grid, LinearProgress, MenuItem, Stack, Tab, Tabs, TextField } from '@mui/material'
import OpenInNewIcon from '@mui/icons-material/OpenInNew'
import EditOutlinedIcon from '@mui/icons-material/EditOutlined'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import { useSnackbar } from 'notistack'
import PageTitle from '../../../components/PageTitle'
import { Pill, SectionCard, ActionIconBtn, helperProps } from '../components/common'
import DataTable from '../components/Tables'
import StandardRateCardsAdmin from './StandardRateCardsAdmin'
import { fmtDateTime } from '../apiMap'
import {
  useGetToolkitQuery, useAddToolkitMutation, useUpdateToolkitMutation, useDeleteToolkitMutation,
} from '../../../redux/d2cApiSlice'

const TYPES = ['Deck', 'Template', 'Case Study', 'Pricing Tool']
const SERVICES = ['ALL', 'FWD_SURFACE', 'FWD_AIR', 'SDD_NDD', 'RVP']
const EMPTY = { name: '', type: 'Deck', service: 'ALL', link: '', remark: '' }

export default function GTMToolkit() {
  const { enqueueSnackbar } = useSnackbar()
  const { data, isLoading, isError, error } = useGetToolkitQuery()
  const [addItem, addState] = useAddToolkitMutation()
  const [updateItem, updateState] = useUpdateToolkitMutation()
  const [deleteItem] = useDeleteToolkitMutation()
  const rows = Array.isArray(data) ? data : []

  const [tab, setTab] = useState(0) // 0 = Toolkit Library, 1 = Build Standard Rate Card
  const [open, setOpen] = useState(false)
  const [editId, setEditId] = useState(null)
  const [form, setForm] = useState(EMPTY)
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }))
  const busy = addState.isLoading || updateState.isLoading

  const [nameErr, setNameErr] = useState(false)
  const startAdd = () => { setEditId(null); setForm(EMPTY); setNameErr(false); setOpen(true) }
  const startEdit = (r) => { setEditId(r.id); setForm({ name: r.name || '', type: r.type || 'Deck', service: r.service || 'ALL', link: r.link || '', remark: r.remark || '' }); setNameErr(false); setOpen(true) }
  const submit = async () => {
    if (!form.name.trim()) { setNameErr(true); return }
    try {
      if (editId) await updateItem({ id: editId, body: form }).unwrap()
      else await addItem(form).unwrap()
      enqueueSnackbar(editId ? 'Toolkit item updated.' : 'Toolkit item added.', { variant: 'success' })
      setOpen(false); setEditId(null); setForm(EMPTY)
    } catch (e) { enqueueSnackbar(e?.data?.message || e?.message || 'Save failed (Functional-Head only).', { variant: 'error' }) }
  }
  const remove = async (r) => {
    try { await deleteItem(r.id).unwrap(); enqueueSnackbar('Toolkit item deleted.', { variant: 'info' }) }
    catch (e) { enqueueSnackbar(e?.data?.message || e?.message || 'Delete failed (Functional-Head only).', { variant: 'error' }) }
  }

  const columns = [
    { key: 'name', label: 'Item', render: (r) => <b>{r.name}</b> },
    { key: 'type', label: 'Type', render: (r) => <Pill label={r.type} styleKey="purple" /> },
    { key: 'service', label: 'Service' },
    { key: 'remark', label: 'Remark' },
    { key: 'updatedAt', label: 'Updated', render: (r) => fmtDateTime(r.updatedAt) },
    {
      key: 'a', label: 'Action', align: 'right', render: (r) => (
        <Stack direction="row" spacing={1.5} justifyContent="flex-end">
          {r.link && <ActionIconBtn title="Open ↗" onClick={() => window.open(r.link, '_blank', 'noopener')}><OpenInNewIcon fontSize="small" /></ActionIconBtn>}
          <ActionIconBtn title="Edit" onClick={() => startEdit(r)}><EditOutlinedIcon fontSize="small" /></ActionIconBtn>
          <ActionIconBtn title="Delete" color="error" onClick={() => remove(r)}><DeleteOutlineIcon fontSize="small" /></ActionIconBtn>
        </Stack>
      ),
    },
  ]

  return (
    <Box sx={{ px: 3 }}>
      <PageTitle>GTM Toolkit</PageTitle>
      <Tabs value={tab} onChange={(_e, v) => setTab(v)} sx={{ mb: 1.5, minHeight: 36 }}>
        <Tab label="📚 Toolkit Library" sx={{ textTransform: 'none', minHeight: 36 }} />
        <Tab label="🗂 Standard Rate Cards" sx={{ textTransform: 'none', minHeight: 36 }} />
      </Tabs>
      {tab === 1 ? <StandardRateCardsAdmin /> : (
      <SectionCard
        title="GTM Toolkit"
        subtitle="Rate cards, decks & collateral — maintained by the Functional Head and visible to all sales leaders."
        action={<Button variant="outlined" sx={{ textTransform: 'none', borderRadius: '6px' }} onClick={() => (open ? setOpen(false) : startAdd())}>{open ? 'Close' : '＋ Add to toolkit'}</Button>}
      >
        <Collapse in={open} unmountOnExit>
          <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: '10px', p: 2, mb: 2, bgcolor: '#FAFAFC' }}>
            <Grid container spacing={1.5}>
              <Grid item xs={12} md={4}><TextField fullWidth required size="small" label="Name" value={form.name} onChange={(e) => { setNameErr(false); set('name')(e) }} error={nameErr} {...helperProps(nameErr ? 'Name is required.' : undefined)} /></Grid>
              <Grid item xs={6} md={4}><TextField fullWidth select size="small" label="Type" value={form.type} onChange={set('type')}>{TYPES.map((t) => <MenuItem key={t} value={t}>{t}</MenuItem>)}</TextField></Grid>
              <Grid item xs={6} md={4}><TextField fullWidth select size="small" label="Service" value={form.service} onChange={set('service')}>{SERVICES.map((s) => <MenuItem key={s} value={s}>{s}</MenuItem>)}</TextField></Grid>
              <Grid item xs={12} md={6}><TextField fullWidth size="small" label="Link (URL)" value={form.link} onChange={set('link')} placeholder="https://…" /></Grid>
              <Grid item xs={12} md={6}><TextField fullWidth size="small" label="Remark" value={form.remark} onChange={set('remark')} /></Grid>
            </Grid>
            <Stack direction="row" spacing={1} sx={{ mt: 1.5 }}>
              <Button variant="contained" sx={{ textTransform: 'none', borderRadius: '6px' }} disabled={busy} onClick={submit}>{busy ? 'Saving…' : (editId ? 'Save changes' : '＋ Add to toolkit')}</Button>
              <Button variant="text" sx={{ textTransform: 'none' }} onClick={() => { setOpen(false); setEditId(null); setForm(EMPTY) }}>Cancel</Button>
            </Stack>
          </Box>
        </Collapse>
        {isLoading && <LinearProgress sx={{ my: 1 }} />}
        {isError && <Alert severity="error" sx={{ my: 1 }}>{error?.message || 'Failed to load the toolkit.'}</Alert>}
        <DataTable columns={columns} rows={rows} getRowKey={(r) => r.id} emptyText={isLoading ? 'Loading…' : 'No toolkit items yet'} />
      </SectionCard>
      )}
    </Box>
  )
}
